import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useT } from '@/contexts/I18nContext';

import { type ChecklistFields, checklistNotDoneColor, serializeChecklist } from './checklistBlock';

/** Edits the "Чеклист" section (checklist.py's build_checklist_report_section):
 *  two comma-separated task-name lists, plus the "настроить чеклист" link.
 *  Colour/underline on "Не выполнено" is fully derived from the two lists
 *  (checklistNotDoneColor) — same convention as TreeQueueForms.tsx's
 *  NoVisitorsForm, no colour picker here either. */
export function ChecklistForm({
  initial,
  onApply,
  onCancel,
}: {
  initial: ChecklistFields;
  onApply: (rows: string[]) => void;
  onCancel: () => void;
}) {
  const { t } = useT();
  const [f, setF] = useState<ChecklistFields>(initial);
  const set = (patch: Partial<ChecklistFields>) => setF((prev) => ({ ...prev, ...patch }));
  const previewRows = serializeChecklist(f);
  const color = checklistNotDoneColor(f);

  return (
    <div className="flex flex-col gap-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {color} {t('reportValidation.tf.checklist')}
      </div>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{t('reportValidation.tree.checklistDone')}</span>
        <textarea
          className="min-h-16 rounded-md border border-border bg-background p-2 text-sm"
          value={f.doneNames.join(', ')}
          onChange={(e) =>
            set({ doneNames: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })
          }
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{t('reportValidation.tree.checklistNotDone')}</span>
        <textarea
          className="min-h-16 rounded-md border border-border bg-background p-2 text-sm"
          value={f.notDoneNames.join(', ')}
          onChange={(e) =>
            set({ notDoneNames: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })
          }
        />
      </label>

      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">{t('reportValidation.form.preview')}</span>
        <div className="report-body rounded-md border border-border bg-muted/40 p-2 text-sm">
          {previewRows.map((row, i) => (
            <div key={i} dangerouslySetInnerHTML={{ __html: row }} />
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('reportValidation.form.cancel')}
        </Button>
        <Button type="button" size="sm" onClick={() => onApply(previewRows)}>
          {t('reportValidation.form.apply')}
        </Button>
      </div>
    </div>
  );
}
